/**
 * Multi-Asset Monte Carlo simulation utility for investment projections.
 * This model simulates stocks, bonds, and inflation as separate but correlated asset classes.
 * ALL returns in the engine are REAL (above inflation). Document this clearly.
 */

import { calculateGlidepath } from './calculations/advancedStrategies';

// Core financial parameters for major asset classes and economic factors
// All means are ARITHMETIC (not geometric/CAGR). The log-normal drift formula converts to geometric.
// Sources: Ibbotson SBBI 1926-2024, real (inflation-adjusted) returns.
export const ASSET_CLASS_PARAMS = {
  stocks: {
    // Long-run US equities: about 6.7% a year above inflation compounded, with
    // about 19% annual volatility. The arithmetic mean that produces it:
    mean: 0.0865,
    stdDev: 0.19,
  },
  bonds: {
    meanReal: 0.025, // 2.5% real arithmetic mean for intermediate-term bonds
    stdDev: 0.07,
    // Real return lost per point of inflation above expectations: the coupon
    // is fixed in nominal terms and prices fall as yields follow inflation up.
    // Because inflation persists, this is what gives bonds their long bad
    // stretches (1940s to 1970s) that year-to-year volatility alone misses.
    inflationSensitivity: -1.5,
  },
  inflation: {
    mean: 0.03,
    // Yearly shock size. With the mean reversion below this gives inflation a
    // long-run spread of about 3 points, in line with US history.
    stdDev: 0.02,
  },
};

// Stocks and bonds have moved together in some decades and apart in others;
// over the long run their real returns are close to uncorrelated.
// Drift added in stronger and weaker market periods. Stronger periods last
// about four times as long, so these are weighted to average to zero and leave
// the long-run return at the figure above.
const REGIME_DRIFT = { bull: 0.002, bear: -0.0075 };

export const CORRELATIONS = {
  stock_bond: 0,
};

// The rest of the model in one place, for the screens and the report that
// describe it. These are the figures written into the two generators below
// (`runSimulationPath` and `generateSequences`): change them together.
export const MARKET_MODEL = {
  // Stronger and weaker stretches alternate; each path draws their lengths once.
  strongYears: [4, 7],
  weakYears: [1, 2],
  // Yearly drift added to the stock return (in log terms) in each kind of stretch.
  regimeDrift: REGIME_DRIFT,
  // Share of the gap to its average that inflation closes each year.
  inflationPull: 0.25,
  // A single year's return is held within these limits.
  stockLimits: [-0.50, 0.60],
  bondLimits: [-0.20, 0.30],
};

// meanReturn = the median simulated real CAGR for each allocation over 30
// years (measured from 20,000 paths). Update these if the parameters change.
export const RISK_PROFILES = {
  conservative: { stocks: 0.30, bonds: 0.70, meanReturn: 4.0,  worstYear: -22, bestYear: 28,  maxDrawdown: -35 },
  balanced:     { stocks: 0.60, bonds: 0.40, meanReturn: 5.4,  worstYear: -33, bestYear: 38,  maxDrawdown: -45 },
  growth:       { stocks: 0.80, bonds: 0.20, meanReturn: 6.1,  worstYear: -42, bestYear: 48,  maxDrawdown: -52 },
  aggressive:   { stocks: 1.00, bonds: 0.00, meanReturn: 6.6,  worstYear: -50, bestYear: 55,  maxDrawdown: -60 },
};

// Historical validation benchmarks, kept for reference only (no longer used to
// clamp simulated paths — see the note in runSimulationPath).
export const HISTORICAL_VALIDATION = {
  minLongTermPortfolioReturn: -0.01,
  maxDrawdown: -0.60,
};

// Random source for every draw in this module. Swapped for a seeded generator
// while a seeded run is in progress so the same inputs give the same answer.
let random = Math.random;

function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// FNV-1a hash of a string to a 32-bit seed.
export function hashSeed(text) {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function withSeed(seed, fn) {
  if (seed == null) return fn();
  const previous = random;
  random = mulberry32(seed);
  try {
    return fn();
  } finally {
    random = previous;
  }
}

/**
 * Generate a random number from a standard normal distribution
 * using the Box-Muller transform.
 * @returns {number} A random number from the standard normal distribution.
 */
const generateStandardNormal = () => {
  let u1 = 0, u2 = 0;
  while (u1 === 0) u1 = random();
  while (u2 === 0) u2 = random();
  return Math.sqrt(-2.0 * Math.log(u1)) * Math.cos(2.0 * Math.PI * u2);
};

/**
 * Runs a single investment simulation path with correlated assets.
 * @param {Object} params - The simulation parameters.
 * @returns {Object} The results of the single simulation path.
 */
const runSimulationPath = ({
  initialInvestment,
  years,
  annualContribution,
  savingsGrowthRate,
  contributions = null,
  allocation,
  inflationMean,
  glidepath = null,
  spendingShocks = null,
  stockShares = null,
  returnShift = 0,
}) => {
  // Destructure parameters for stocks, bonds, and inflation
  const { stocks: stockParams, bonds: bondParams, inflation: inflationDefaults } = ASSET_CLASS_PARAMS;
  // Allow caller to override the inflation mean; fall back to the default
  const inflationParams = {
    ...inflationDefaults,
    mean: inflationMean != null ? inflationMean : inflationDefaults.mean,
  };
  const { stock_bond: stockBondCorrelation } = CORRELATIONS;
  
  // Calculate log-normal drift for equities
  const stockDrift = Math.log(1 + stockParams.mean) - 0.5 * Math.pow(stockParams.stdDev, 2);

  // Initialize arrays to track portfolio values and returns
  const nominalYearlyValues = [initialInvestment];
  const realYearlyValues = [initialInvestment];
  const yearlyReturns = [];
  
  // State variables for the simulation loop
  let currentNominalValue = initialInvestment;
  let peakValue = initialInvestment;
  let maxDrawdown = 0;
  let cumulativeInflation = 1.0; // Track path-specific cumulative inflation
  
  // Behavioral model state variables (driven by equity performance)
  let inBullMarket = true;
  let yearsInCurrentMarket = 0;
  const bullMarketLength = Math.floor(4 + random() * 4); // 4-7 years
  const bearMarketLength = Math.floor(1 + random() * 2); // 1-2 years
  
  for (let year = 1; year <= years; year++) {
    // 1. SIMULATE ECONOMIC CONDITIONS FOR THE YEAR
    // Generate independent random numbers for each factor
    const zStock = generateStandardNormal();
    const zBondInd = generateStandardNormal();
    const zInflation = generateStandardNormal();

    // Create correlated random number for bonds
    const zBond = stockBondCorrelation * zStock + Math.sqrt(1 - stockBondCorrelation ** 2) * zBondInd;

    // Simulate inflation for the year using Ornstein-Uhlenbeck mean-reversion process
    const lastInflation = year > 1 ? yearlyReturns[year - 2]?.inflation || inflationParams.mean : inflationParams.mean;
    // O-U: inflation_t = lastInflation + 0.25*(mean - lastInflation) + sigma*z
    const currentInflation = Math.max(-0.05,
      lastInflation + 0.25 * (inflationParams.mean - lastInflation) + inflationParams.stdDev * zInflation
    );
    cumulativeInflation *= (1 + currentInflation); // Update the cumulative inflation factor

    // 2. SIMULATE ASSET RETURNS
    // Use a simplified behavioral model based on equity market cycles
    yearsInCurrentMarket++;
    if (inBullMarket && yearsInCurrentMarket > bullMarketLength) {
      inBullMarket = false;
      yearsInCurrentMarket = 1;
    } else if (!inBullMarket && yearsInCurrentMarket > bearMarketLength) {
      inBullMarket = true;
      yearsInCurrentMarket = 1;
    }
    
    // Equities drive the behavioral cycle - much smaller adjustments
    const regimeAdjustment = inBullMarket ? REGIME_DRIFT.bull : REGIME_DRIFT.bear;
    let stockReturnDrift = stockDrift + regimeAdjustment;
    let stockReturn;

    // Generate base stock return using log-normal distribution
    stockReturn = Math.exp(stockReturnDrift + stockParams.stdDev * zStock) - 1;

    // --- MINIMAL BEHAVIORAL ADJUSTMENTS ---

    stockReturn = Math.max(-0.50, Math.min(0.60, stockReturn));

    // Bond return is purely REAL (no inflation added to drift), consistent with stock returns
    const expectedBondReturn = bondParams.meanReal;
    const bondDrift = Math.log(1 + expectedBondReturn) - 0.5 * bondParams.stdDev ** 2;
    let bondReturn = Math.exp(bondDrift + bondParams.stdDev * zBond) - 1;

    // Adjust for inflation surprise (unexpected inflation hurts real bond returns)
    const inflationSurprise = currentInflation - inflationParams.mean;
    bondReturn += inflationSurprise * bondParams.inflationSensitivity;
    bondReturn = Math.max(-0.20, Math.min(0.30, bondReturn));

    // 3. CALCULATE PORTFOLIO RETURN (on existing balance only)
    let stockPct = allocation.stocks;
    let bondPct = allocation.bonds;
    if (glidepath && year >= (glidepath.retirementYear || 0)) {
      const glideYear = year - (glidepath.retirementYear || 0);
      stockPct = calculateGlidepath({
        startStockPct: glidepath.startStockPct,
        endStockPct: glidepath.endStockPct,
        glidepathYears: glidepath.glidepathYears,
        currentYear: glideYear,
      });
      bondPct = 1 - stockPct;
    }
    if (stockShares) {
      stockPct = stockShares[Math.min(year - 1, stockShares.length - 1)];
      bondPct = 1 - stockPct;
    }
    // `returnShift` is the yearly cost of investing and any view on returns.
    const portfolioReturn = (stockReturn * stockPct) + (bondReturn * bondPct) + returnShift;

    // Apply investment returns to current portfolio value
    currentNominalValue *= (1 + portfolioReturn);

    // Subtract any spending shocks for this year
    currentNominalValue -= (spendingShocks?.[year] || 0);

    // THEN add annual contribution at the END of the year (growing over time)
    // This ensures contributions don't affect the return calculation
    // A year-by-year schedule, when given, replaces the growing amount.
    if (contributions) {
      currentNominalValue += contributions[year - 1] || 0;
    } else if (annualContribution > 0) {
      const growingContribution = annualContribution * Math.pow(1 + savingsGrowthRate, year - 1);
      currentNominalValue += growingContribution;
    }
    if (currentNominalValue < 0) currentNominalValue = 0;
    
    // Store this year's results
    yearlyReturns.push({ portfolio: portfolioReturn, stock: stockReturn, bond: bondReturn, inflation: currentInflation });

    // currentNominalValue tracks real purchasing power (real returns + real contributions).
    // Nominal = what you'd see in your brokerage (real value * cumulative price inflation).
    const realValue = currentNominalValue;
    const nominalValue = currentNominalValue * cumulativeInflation;

    // Update drawdown (tracked in real terms)
    peakValue = Math.max(peakValue, realValue);
    // Someone starting from nothing has no peak to fall from yet.
    if (peakValue > 0) maxDrawdown = Math.min(maxDrawdown, (realValue - peakValue) / peakValue);

    realYearlyValues.push(realValue);
    nominalYearlyValues.push(nominalValue);
  }
  
  // Final calculations for the path
  const finalNominalValue = nominalYearlyValues[years];
  const finalRealValue = realYearlyValues[years];
  
  // Calculate CAGR based on investment returns only (excluding contribution effects)
  // Since both stock and bond returns are REAL, this is a true real CAGR
  const totalPortfolioReturn = yearlyReturns.reduce((product, yearData) => product * (1 + yearData.portfolio), 1);
  const realCAGR = Math.pow(totalPortfolioReturn, 1 / years) - 1;

  // Nominal CAGR uses the input inflation mean, not this path's random inflation
  const nominalCAGR = (1 + realCAGR) * (1 + inflationParams.mean) - 1;

  // NOTE: A previous long-horizon "guardrail" rescaled any path with realCAGR
  // below minLongTermPortfolioReturn up to that floor. It censored the genuine
  // left tail (the very paths a planner needs to see) and mathematically
  // over-scaled contributed dollars. Removed so the downside is reported
  // honestly; the per-year crash dampener already bounds pathological crashes.
  return {
    finalNominalValue,
    finalRealValue,
    nominalCAGR,
    realCAGR,
    wasAdjusted: false,
    maxDrawdown, // Track actual path drawdown (not floored by historical validation)
    nominalYearlyValues,
    realYearlyValues,
    yearlyReturns: yearlyReturns.map(r => r.portfolio),
  };
};

/**
 * Runs a complete Monte Carlo simulation with multiple paths.
 * @param {Object} params - The simulation parameters.
 * @returns {Object} The aggregated simulation results.
 */
// Deterministic growth to retirement using the same contribution timing as the
// simulation: savings are added at year end and grow at savingsGrowthRate.
export function projectPortfolioToRetirement({ balance, annualSavings = 0, savingsGrowthRate = 0, years, annualReturn }) {
  let value = balance;
  for (let year = 1; year <= years; year++) {
    value = value * (1 + annualReturn) + annualSavings * Math.pow(1 + savingsGrowthRate, year - 1);
  }
  return Math.round(value);
}

// `pathSeed` gives each path its own stream, exactly as generateCorrelatedSequences
// does, so path 17 here is the same market as path 17 in the retirement plan.
export const runMonteCarloSimulation = ({ seed, pathSeed = null, ...params }) =>
  withSeed(pathSeed != null ? pathSeed : seed, () => runSimulation({ ...params, pathSeed }));

const runSimulation = ({
  initialInvestment = 100000,
  years = 30,
  annualContribution = 0,
  savingsGrowthRate = 0,
  contributions = null, // Per-year amounts in today's dollars; overrides the two above
  riskProfile = 'balanced',
  numberOfSimulations = 10000,
  inflationRate, // Used as the mean for stochastic inflation (O-U process center)
  startingAge = 25, // Starting age for time series display
  glidepath = null, // { startStockPct, endStockPct, glidepathYears, retirementYear }
  spendingShocks = null, // Array of per-year shock amounts indexed by year
  pathSeed = null,
  stockShares = null, // Share in stocks for each year, when it changes with age
  returnShift = 0, // Added to every year's return: costs (negative) and any view on returns
}) => {
  // Get the asset allocation for the selected risk profile
  const allocation = RISK_PROFILES[riskProfile];
  if (!allocation) {
    throw new Error(`Invalid risk profile: ${riskProfile}`);
  }

  // Use inflationRate as the inflation mean if provided, otherwise engine default (~3%)
  const inflationMean = inflationRate != null ? inflationRate : undefined;

  const allSimulations = [];
  for (let i = 0; i < numberOfSimulations; i++) {
    if (pathSeed != null) random = mulberry32((pathSeed + Math.imul(i + 1, 0x9E3779B1)) >>> 0);
    const path = runSimulationPath({
      initialInvestment,
      years,
      annualContribution,
      savingsGrowthRate,
      contributions,
      allocation,
      inflationMean,
      glidepath,
      spendingShocks,
      stockShares,
      returnShift,
    });
    allSimulations.push(path);
  }

  // --- AGGREGATE RESULTS ---
  // Sort by real final value (purchasing power) for percentile selection
  const nominalFinalValueSorted = [...allSimulations].sort((a, b) => a.finalRealValue - b.finalRealValue);
  const drawdownSorted = [...allSimulations].sort((a, b) => a.maxDrawdown - b.maxDrawdown);
  
  // Calculate percentile paths
  // The same percentile positions as the retirement plan uses.
  const p1Index = Math.floor(numberOfSimulations * 0.01);
  const p10Index = Math.floor(numberOfSimulations * 0.1);
  const p25Index = Math.floor(numberOfSimulations * 0.25);
  const medianIndex = Math.floor(numberOfSimulations / 2);
  const p75Index = Math.min(numberOfSimulations - 1, Math.floor(numberOfSimulations * 0.75));
  const p90Index = Math.min(numberOfSimulations - 1, Math.floor(numberOfSimulations * 0.9));
  const p99Index = Math.min(numberOfSimulations - 1, Math.floor(numberOfSimulations * 0.99));

  const p1Case = nominalFinalValueSorted[p1Index];
  const p10Case = nominalFinalValueSorted[p10Index];
  const p25Case = nominalFinalValueSorted[p25Index];
  const medianCase = nominalFinalValueSorted[medianIndex];
  const p75Case = nominalFinalValueSorted[p75Index];
  const p90Case = nominalFinalValueSorted[p90Index];
  const p99Case = nominalFinalValueSorted[p99Index];
  const worstAbsoluteCase = nominalFinalValueSorted[0];

  // Median of every path's CAGR. Taking the CAGR of the one path that happens
  // to have the median final value is noisy from run to run.
  const sortedRealCAGRs = allSimulations.map(s => s.realCAGR).sort((a, b) => a - b);
  const medianRealCAGR = sortedRealCAGRs[medianIndex];
  const medianNominalCAGR = (1 + medianRealCAGR) * (1 + (inflationMean ?? ASSET_CLASS_PARAMS.inflation.mean)) - 1;

  // Aggregate statistics
  const avgAnnualReturn = allSimulations.reduce((sum, s) => sum + (s.yearlyReturns.reduce((a, b) => a + b, 0) / s.yearlyReturns.length), 0) / numberOfSimulations;
  const avgMaxDrawdown = allSimulations.reduce((sum, s) => sum + s.maxDrawdown, 0) / numberOfSimulations;
  
  // Collect a sample of paths for background visualization
  const allPaths = [];
  const maxPathsToStore = Math.min(500, numberOfSimulations);
  const pathStep = Math.max(1, Math.floor(numberOfSimulations / maxPathsToStore));
  for (let i = 0; i < numberOfSimulations; i += pathStep) {
    allPaths.push(allSimulations[i].realYearlyValues);
  }
  
  // Generate time series data for the median case for charts
  const timeSeriesData = [];
  for (let year = 0; year <= years; year++) {
    timeSeriesData.push({
      year: year,
      portfolioValue: medianCase.realYearlyValues[year] || 0,
      age: startingAge + year,
    });
  }

  // --- PREPARE FINAL RESULTS OBJECT ---
  return {
    medianPath: medianCase.realYearlyValues,
    worstPath: p1Case.realYearlyValues,
    bestPath: p99Case.realYearlyValues,
    optimisticPath: p90Case.realYearlyValues,
    
    nominalMedianPath: medianCase.realYearlyValues.map((v, i) => v * Math.pow(1 + (inflationMean ?? ASSET_CLASS_PARAMS.inflation.mean), i)),
    nominalWorstPath: p1Case.realYearlyValues.map((v, i) => v * Math.pow(1 + (inflationMean ?? ASSET_CLASS_PARAMS.inflation.mean), i)),
    nominalBestPath: p99Case.realYearlyValues.map((v, i) => v * Math.pow(1 + (inflationMean ?? ASSET_CLASS_PARAMS.inflation.mean), i)),
    
    worstDrawdownPath: drawdownSorted[0].realYearlyValues,
    allPaths,
    timeSeriesData,
    
    yearlyReturns: {
      worst: p1Case.yearlyReturns,
      absoluteWorst: worstAbsoluteCase.yearlyReturns,
      p1: p1Case.yearlyReturns,
      p10: p10Case.yearlyReturns,
      median: medianCase.yearlyReturns,
      p90: p90Case.yearlyReturns,
      optimistic: p90Case.yearlyReturns,
      p99: p99Case.yearlyReturns,
      best: p99Case.yearlyReturns,
    },
    
    finalValues: {
      median: medianCase.finalRealValue,
      worst: p1Case.finalRealValue,
      absoluteWorst: worstAbsoluteCase.finalRealValue,
      optimistic: p90Case.finalRealValue,
      best: p99Case.finalRealValue,
    },
    
    nominalFinalValues: {
      median: medianCase.finalRealValue * Math.pow(1 + (inflationMean ?? ASSET_CLASS_PARAMS.inflation.mean), years),
      worst: p1Case.finalRealValue * Math.pow(1 + (inflationMean ?? ASSET_CLASS_PARAMS.inflation.mean), years),
      optimistic: p90Case.finalRealValue * Math.pow(1 + (inflationMean ?? ASSET_CLASS_PARAMS.inflation.mean), years),
      best: p99Case.finalRealValue * Math.pow(1 + (inflationMean ?? ASSET_CLASS_PARAMS.inflation.mean), years),
    },
    
    drawdowns: {
      average: avgMaxDrawdown,
      worst: drawdownSorted[0].maxDrawdown,
    },
    
    percentiles: {
      p1: p1Case.finalRealValue,
      p10: p10Case.finalRealValue,
      p25: p25Case.finalRealValue,
      p50: medianCase.finalRealValue,
      p75: p75Case.finalRealValue,
      p90: p90Case.finalRealValue,
      p99: p99Case.finalRealValue,
    },
    
    medianCAGR: medianRealCAGR,
    nominalMedianCAGR: medianNominalCAGR,
    avgAnnualReturn: avgAnnualReturn,

    percentileCAGRs: {
      p1: p1Case.realCAGR,
      p10: p10Case.realCAGR,
      p25: p25Case.realCAGR,
      p50: medianCase.realCAGR,
      p75: p75Case.realCAGR,
      p90: p90Case.realCAGR,
      p99: p99Case.realCAGR,
    },
  };
};

/**
 * Generates correlated return sequences using the full regime/correlation model.
 * Lightweight version of runSimulationPath — produces only yearly portfolio returns,
 * skipping portfolio value tracking, contributions, and drawdown calculation.
 * Includes: bull/bear regimes, stock-bond correlation, O-U inflation, inflation surprise on bonds.
 */
export function generateCorrelatedSequences({ seed, ...params }) {
  return withSeed(seed, () => generateSequences({ ...params, pathSeed: seed }));
}

function generateSequences({ years, riskProfile = 'balanced', inflationMean, numSims = 500, pathSeed = null, stockShares = null }) {
  const allocation = RISK_PROFILES[riskProfile];
  if (!allocation) throw new Error(`Invalid risk profile: ${riskProfile}`);

  const { stocks: stockParams, bonds: bondParams, inflation: inflationDefaults } = ASSET_CLASS_PARAMS;
  const inflationParams = { ...inflationDefaults, mean: inflationMean != null ? inflationMean : inflationDefaults.mean };
  const { stock_bond: stockBondCorrelation } = CORRELATIONS;
  const stockDrift = Math.log(1 + stockParams.mean) - 0.5 * stockParams.stdDev ** 2;
  const bondDrift = Math.log(1 + bondParams.meanReal) - 0.5 * bondParams.stdDev ** 2;

  const allPaths = [];

  for (let s = 0; s < numSims; s++) {
    // Each path draws from its own stream, so path 17 is the same market
    // history whatever the horizon or the number of paths asked for.
    if (pathSeed != null) random = mulberry32((pathSeed + Math.imul(s + 1, 0x9E3779B1)) >>> 0);
    const returns = [];
    let inBullMarket = true;
    let yearsInCurrentMarket = 0;
    const bullLen = Math.floor(4 + random() * 4);
    const bearLen = Math.floor(1 + random() * 2);
    let lastInflation = inflationParams.mean;

    for (let y = 0; y < years; y++) {
      const zStock = generateStandardNormal();
      const zBondInd = generateStandardNormal();
      const zInflation = generateStandardNormal();
      const zBond = stockBondCorrelation * zStock + Math.sqrt(1 - stockBondCorrelation ** 2) * zBondInd;

      const currentInflation = Math.max(-0.05,
        lastInflation + 0.25 * (inflationParams.mean - lastInflation) + inflationParams.stdDev * zInflation
      );
      lastInflation = currentInflation;

      yearsInCurrentMarket++;
      if (inBullMarket && yearsInCurrentMarket > bullLen) { inBullMarket = false; yearsInCurrentMarket = 1; }
      else if (!inBullMarket && yearsInCurrentMarket > bearLen) { inBullMarket = true; yearsInCurrentMarket = 1; }

      const regimeAdj = inBullMarket ? REGIME_DRIFT.bull : REGIME_DRIFT.bear;
      let stockReturn = Math.exp((stockDrift + regimeAdj) + stockParams.stdDev * zStock) - 1;

      stockReturn = Math.max(-0.50, Math.min(0.60, stockReturn));

      let bondReturn = Math.exp(bondDrift + bondParams.stdDev * zBond) - 1;
      bondReturn += (currentInflation - inflationParams.mean) * bondParams.inflationSensitivity;
      bondReturn = Math.max(-0.20, Math.min(0.30, bondReturn));

      // The share in stocks can change from year to year (a mix that shifts with age).
      const stocks = stockShares ? stockShares[Math.min(y, stockShares.length - 1)] : allocation.stocks;
      returns.push(stockReturn * stocks + bondReturn * (1 - stocks));
    }
    allPaths.push(returns);
  }

  const endValues = allPaths.map((path, idx) => {
    let val = 1;
    for (const r of path) val *= (1 + r);
    return { idx, val };
  });
  endValues.sort((a, b) => a.val - b.val);

  const pick = (pct) => allPaths[endValues[Math.floor(pct * numSims / 100)].idx];

  return {
    pessimistic: pick(10),
    median: pick(50),
    optimistic: pick(90),
    allPaths,
  };
} 